import { Badge, Spinner, makeStyles, tokens } from '@coreai/fluentui-react';
import type { MonitoringStatus } from '@/types/monitoring';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  spinnerMargin: {
    marginRight: tokens.spacingHorizontalXS,
  },
  cancelledBadge: {
    backgroundColor: ottoTokens.statusCancelled,
    color: tokens.colorNeutralForegroundOnBrand,
    borderTopColor: ottoTokens.statusCancelled,
    borderRightColor: ottoTokens.statusCancelled,
    borderBottomColor: ottoTokens.statusCancelled,
    borderLeftColor: ottoTokens.statusCancelled,
  },
  cancelledDot: {
    backgroundColor: ottoTokens.statusCancelled,
    borderTopColor: ottoTokens.statusCancelled,
    borderRightColor: ottoTokens.statusCancelled,
    borderBottomColor: ottoTokens.statusCancelled,
    borderLeftColor: ottoTokens.statusCancelled,
  },
  // 'Skipped' is shown in neutral grey — it's not a failure, just a no-op
  // trigger evaluation. Using neutral rather than `informative` (which is
  // blue and reads as "in progress") avoids implying activity.
  skippedBadge: {
    backgroundColor: tokens.colorNeutralBackground5,
    color: tokens.colorNeutralForeground3,
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  skippedDot: {
    backgroundColor: tokens.colorNeutralBackground5,
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
});

type RunStatusBadgeProps = {
  // Accepts the wider MonitoringStatus union so the same badge can render
  // both run rows (succeeded/failed/cancelled/running) and trigger history
  // rows (which additionally use 'skipped').
  status: MonitoringStatus;
  size?: 'small' | 'medium' | 'large';
  /**
   * When true, render only a colored dot (no label text). The accessible name
   * is preserved via a visually-hidden span so screen readers still announce
   * the status. Use at narrow widths where the labelled badge would clip.
   */
  compact?: boolean;
};

const getAppearance = (
  status: MonitoringStatus
): 'success' | 'danger' | 'warning' | 'informative' | 'subtle' => {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'warning';
    case 'skipped':
      return 'subtle';
    case 'running':
    default:
      return 'informative';
  }
};

const getLabel = (status: MonitoringStatus): string => {
  switch (status) {
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    case 'skipped':
      return 'Skipped';
    case 'running':
      return 'Running';
    default:
      return status;
  }
};

export const RunStatusBadge = ({ status, size = 'medium', compact = false }: RunStatusBadgeProps) => {
  const styles = useStyles();
  const label = getLabel(status);
  const color = getAppearance(status);

  const customDotClass =
    status === 'cancelled'
      ? styles.cancelledDot
      : status === 'skipped'
      ? styles.skippedDot
      : undefined;
  const customBadgeClass =
    status === 'cancelled'
      ? styles.cancelledBadge
      : status === 'skipped'
      ? styles.skippedBadge
      : undefined;

  if (compact) {
    // Dot-only variant: a tiny filled Badge with no children renders as a
    // colored circle. Spinner is preserved for the running state so motion
    // still communicates "in progress" without a text label. `aria-label`
    // provides the accessible name; `title` provides the hover tooltip.
    return (
      <Badge
        appearance="filled"
        color={color === 'subtle' ? 'informative' : color}
        size="small"
        data-status={status}
        aria-label={label}
        title={label}
        className={customDotClass}
      >
        {/* Decorative: the badge's aria-label already names the status. */}
        {status === 'running' ? <Spinner size="extra-tiny" aria-hidden="true" /> : null}
      </Badge>
    );
  }

  if (status === 'running') {
    return (
      <Badge appearance="filled" color="informative" size={size} data-status={status}>
        {/* Decorative: the visible "Running" text names the status. */}
        <Spinner size="extra-tiny" className={styles.spinnerMargin} aria-hidden="true" />
        Running
      </Badge>
    );
  }

  return (
    <Badge
      appearance="filled"
      color={color === 'subtle' ? 'informative' : color}
      size={size}
      data-status={status}
      className={customBadgeClass}
    >
      {label}
    </Badge>
  );
};
